/** AFM / Foxline CODE as a string. Never Number() — leading zeros are significant. */
export function normalizeEmployeeCode(value) {
  return value == null ? "" : String(value).trim();
}

/**
 * Canonical AFM plus alternate codes for the same Foxline PRSN
 * (stale CODE rows: leading-zero variants, φ prefix, truncated digits).
 */
export function employeeCodeAliases(emp) {
  const seen = new Set();
  const out = [];
  const add = (value) => {
    const code = normalizeEmployeeCode(value);
    if (!code || seen.has(code)) return;
    seen.add(code);
    out.push(code);
  };
  add(emp?.employee_code);
  const extra = emp?.employee_code_aliases;
  if (Array.isArray(extra)) {
    extra.forEach(add);
  }
  return out;
}

/** Prefer Foxline AFM for schedule map keys; fallback to PRSN `id`. */
export function rosterScheduleKey(emp) {
  if (!emp) return "";
  const code = normalizeEmployeeCode(emp.employee_code);
  return code || String(emp.id ?? "");
}

/** True when emp.id is Foxline PRSN — not a guest placeholder set to employee_code. */
export function isValidFoxlinePrsn(emp) {
  if (!emp) return false;
  const rawId = emp.id;
  if (rawId == null || rawId === "") return false;
  const numId = Number(rawId);
  if (!Number.isFinite(numId) || numId <= 0) return false;
  const code = normalizeEmployeeCode(emp.employee_code);
  if (code && String(rawId).trim() === code) return false;
  return true;
}

export function staffRecordMatchesCode(emp, employeeCode) {
  const code = normalizeEmployeeCode(employeeCode);
  if (!code || !emp) return false;
  return employeeCodeAliases(emp).includes(code);
}

/** First staff record whose AFM or aliases match; requires a real Foxline PRSN. */
export function foxlineStaffRecordForCode(staffPool, employeeCode) {
  const code = normalizeEmployeeCode(employeeCode);
  if (!code) return null;
  for (const emp of staffPool || []) {
    if (!staffRecordMatchesCode(emp, code)) continue;
    if (!isValidFoxlinePrsn(emp)) continue;
    return emp;
  }
  return null;
}

export function resolveFoxlineEmployeeId(staffPool, employeeCode) {
  const emp = foxlineStaffRecordForCode(staffPool, employeeCode);
  return emp ? Number(emp.id) : null;
}

/** Rota row → staff. AFM / aliases only — never display name. */
export function findStaffForRotaRow(staffPool, row) {
  return foxlineStaffRecordForCode(staffPool, row?.employee_code);
}

export function guestRecordForRotaCode(globalEmp, rowCode, row = {}) {
  const code = normalizeEmployeeCode(rowCode);
  const canonical = normalizeEmployeeCode(globalEmp?.employee_code) || code;
  const aliases = employeeCodeAliases({
    employee_code: canonical,
    employee_code_aliases: [
      code,
      ...(Array.isArray(globalEmp?.employee_code_aliases)
        ? globalEmp.employee_code_aliases
        : []),
    ],
  }).filter((item) => item !== canonical);
  return {
    id: globalEmp && isValidFoxlinePrsn(globalEmp) ? Number(globalEmp.id) : null,
    employee_code: canonical || code,
    employee_code_aliases: aliases,
    name: globalEmp?.name || row.employee_name || code,
    store: globalEmp?.store || "",
    branch_name: globalEmp?.branch_name || "",
    group: row.role || globalEmp?.group || "service",
    shift_hours: globalEmp?.shift_hours ?? 8,
    shiftHours: globalEmp?.shift_hours ?? 8,
    work_days: globalEmp?.days_per_week ?? 5,
    workDays: globalEmp?.days_per_week ?? 5,
    _scheduleGuest: true,
  };
}
